/* The Reminders block inside the app's ONE settings surface (App.jsx's
 * SettingsModal). Its own file only so App.jsx doesn't grow another eighty
 * lines — it is not a second settings screen, and must never become one.
 *
 * Three things it deliberately does:
 *
 *  - **Asks for the OS permission at the moment the switch is flipped**, never
 *    on load. The prompt is one-shot on both platforms; spending it on someone
 *    who has not asked for reminders trades a permanent denial for nothing.
 *  - **Refuses to show "on" when nothing can arrive.** A denied permission
 *    leaves the checkbox off and says why. A switch that claims to be on while
 *    the OS drops every notification makes the app look broken instead of
 *    blocked, and the user never finds the real cause.
 *  - **States the web's limit in the web's own words.** The Notification API
 *    can only fire while a tab is open. Letting someone believe otherwise
 *    costs exactly one missed streak.
 */
import { useEffect, useState } from 'react'
import { C, R, T } from './theme.js'
import { sheetSection } from './sheetStyles.js'
import { emailDeliveryAvailable } from './appConfig.js'
import { isNative, notificationPermission } from './native.js'
import { deviceSupport, devicePermission, loadNotifyPrefs, requestDevicePermission, saveNotifyPrefs, syncReminders } from './notify.js'
import { REMIND_TIMES, remindTimeFor, usualRemindAt } from './notifyPrefs.js'
import { loadLearn } from './learn/persist.js'
import { usualHour } from './learn/goals.js'
import { useT } from './i18n.js'

const note = { ...T.caption, color: C.sub, margin: '2px 0 0 26px' }
const check = { ...T.small, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 2px', color: C.ink, cursor: 'pointer' }

export default function NotifySettings({ user }) {
  const translate = useT()
  const [prefs, setPrefs] = useState(loadNotifyPrefs)
  const [permission, setPermission] = useState(() => (isNative ? 'unknown' : devicePermission()))
  const [busy, setBusy] = useState(false)
  /* Undefined until the answer arrives, and the row renders on `true` only —
   * a toggle that appears and then disappears is worse than one that appears
   * a beat late. See appConfig.js for why this is a Firestore read and not a
   * build flag. */
  const [emailSender, setEmailSender] = useState(false)
  const support = deviceSupport()

  useEffect(() => {
    let alive = true
    emailDeliveryAvailable().then((ok) => alive && setEmailSender(ok))
    return () => { alive = false }
  }, [])

  // Native has to ask the plugin; the web answers synchronously above.
  useEffect(() => {
    if (!isNative) return
    let alive = true
    notificationPermission().then((p) => alive && setPermission(p))
    return () => { alive = false }
  }, [])

  const blocked = permission === 'denied'
  const canEmail = Boolean(user?.email && !user.isAnonymous && user.emailVerified !== false)

  /* One path for both switches. Turning something ON has to clear the OS
   * permission first (and may be refused); turning it off never can, so it is
   * immediate and unconditional — an off switch that could fail would be the
   * worst control in the app. */
  const toggle = async (patchFor, on) => {
    if (!on) {
      setPrefs(saveNotifyPrefs(patchFor(false)))
      syncReminders()
      return
    }
    setBusy(true)
    const granted = await requestDevicePermission()
    setPermission(granted)
    setBusy(false)
    // Only record the choice if it can be honored — see the header note.
    if (granted !== 'granted') {
      setPrefs(saveNotifyPrefs({ askedAt: Date.now() }))
      return
    }
    setPrefs(saveNotifyPrefs({ ...patchFor(true), askedAt: Date.now() }))
    syncReminders()
  }
  const togglePractice = (on) => toggle((device) => ({ practice: { device } }), on)
  const toggleRecap = (on) => toggle((device) => ({ recap: { device } }), on)

  /* W2d · until the user picks a time, reminders follow their habit. The select
   * shows the time that will actually be used, and says where it came from. */
  const learnState = loadLearn()
  const effectiveAt = remindTimeFor(prefs, learnState)
  const fromHabit = !prefs.practice.atChosen && Boolean(usualRemindAt(learnState))

  const pickTime = (at) => {
    setPrefs(saveNotifyPrefs({ practice: { at, atChosen: true } }))
    syncReminders()
  }

  return (
    <>
      <div style={sheetSection}>{translate('reminders.title')}</div>
      {support.available ? (
        <div style={{ marginTop: 6 }}>
          <label style={{ ...check, opacity: blocked ? 0.55 : 1 }}>
            <input
              type="checkbox"
              checked={Boolean(prefs.practice.device)}
              disabled={busy || blocked}
              onChange={(e) => togglePractice(e.target.checked)}
              style={{ accentColor: C.brass, height: 17, width: 17 }}
            />
            {translate('reminders.daily')}
          </label>
          {prefs.practice.device ? (
            <div style={{ alignItems: 'center', display: 'flex', gap: 8, margin: '4px 0 0 26px' }}>
              <span style={{ ...T.caption, color: C.sub }}>{translate('reminders.at')}</span>
              <select
                aria-label={translate('reminders.time')}
                value={effectiveAt}
                onChange={(e) => pickTime(e.target.value)}
                style={{ ...T.data, background: C.card, border: `1.5px solid ${C.edge}`, borderRadius: R.sm, color: C.ink, minHeight: 36, padding: '0 8px' }}
              >
                {REMIND_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {fromHabit ? (
                <span data-testid="reminder-usual-time" style={{ ...T.caption, color: C.sub }}>
                  {translate('reminders.usualTime', { hour: `${String(usualHour(learnState)).padStart(2, '0')}:00` })}
                </span>
              ) : null}
            </div>
          ) : null}
          <div style={note}>
            {blocked
              ? translate('reminders.blocked')
              : translate('reminders.onlyUntrained')}
          </div>
          {/* G7's Monday note. Separate from the daily reminder because they
            * are different promises: one defends a habit, one reports a week —
            * and someone who wants the second is not necessarily asking to be
            * nudged every evening. */}
          <label style={{ ...check, opacity: blocked ? 0.55 : 1 }}>
            <input
              type="checkbox"
              checked={Boolean(prefs.recap.device)}
              disabled={busy || blocked}
              onChange={(e) => toggleRecap(e.target.checked)}
              style={{ accentColor: C.brass, height: 17, width: 17 }}
            />
            {translate('reminders.weeklyRecap')}
          </label>
          <div style={note}>{translate('reminders.recapBlurb')}</div>
          {!support.background && !blocked ? (
            <div style={note}>
              {translate('reminders.webOnly')}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ ...note, marginLeft: 0, marginTop: 8 }}>{translate('reminders.unsupported')}</div>
      )}

      {/* Outside the block above, because email needs none of it: a browser
        * that cannot show a notification can still receive one. The row exists
        * only where something can actually SEND (config/notifications) and
        * there is a verified address to send TO — offering it otherwise is a
        * promise the app cannot keep, and an unverified address is somebody
        * else's inbox waiting to happen. */}
      {emailSender && canEmail ? (
        <div style={{ marginTop: support.available ? 0 : 6 }}>
          <label style={check}>
            <input
              type="checkbox"
              checked={Boolean(prefs.recap.email)}
              onChange={(e) => setPrefs(saveNotifyPrefs({ recap: { email: e.target.checked } }))}
              style={{ accentColor: C.brass, height: 17, width: 17 }}
            />
            {translate('reminders.emailRecap')}
          </label>
          <div style={note}>
            {translate('reminders.sentTo', { email: user.email })}
          </div>
        </div>
      ) : null}
    </>
  )
}
